/**
 * @file Planboard grid bar component.
 * @author Heino Lenting
 */
import * as tslib_1 from "tslib";
import { EventEmitter, ElementRef, NgZone, AfterViewInit } from '@angular/core';
import { GridBarBaseComponent } from '../grid-bar-base/grid-bar-base.component';
import { DataSet } from '../../core/helpers/data-set';
import { ExecutorsService } from '../executors.service';
import { AlertsService } from '../../core/alerts.service';
import { OverlayService } from '../../core/overlay.service';
import { EventsService } from '../../core/event.service';
import { LocalStorageService } from '../../core/local-storage.service';
import { DomainService } from '../../domain.service';
import { PropertyFilter } from '../../core/helpers/property-filter';
import { FilterOperators } from '../../core/helpers/filter-operators';
import { GridService } from '../grid.service';
var PlanboardGridBarComponent = /** @class */ (function (_super) {
    tslib_1.__extends(PlanboardGridBarComponent, _super);
    /**
     * Constructs AgendaGridBarComponent.
     * @param configurationsService The configuration service to get the configuration of this component.
     * @param router The router to synchronize query parameters with component state.
     */
    function PlanboardGridBarComponent(executorsService, alertsService, elementRef, ngZone, overlayService, eventsServices, localStorageService, domainService, gridService) {
        var _this = _super.call(this, executorsService, alertsService, elementRef, ngZone, eventsServices, gridService) || this;
        _this.overlayService = overlayService;
        _this.localStorageService = localStorageService;
        _this.domainService = domainService;
        /**
         * Event that triggers when the entity that filters the shown resources is changed.
         * @memberOf PlanboardGridBarComponent
         */
        _this.filterEntityChanged = new EventEmitter();
        /**
         * Gets, sets the reference of the entity responsible for filtering the resources
         * @private
         * @type {Entity}
         * @memberOf PlanboardGridBarComponent
         */
        _this._filterEntity = null;
        /**
         * The names of the available filter sources.
         * @private
         * @memberOf PlanboardGridBarComponent
         */
        _this.filterSources = ['TEAM', 'COMPETENCE', 'REGION', 'RESOURCE', 'PROJECT'];
        /**
         * Gets, sets the flag indicating whether or not the user has changed the filter.
         * @memberOf PlanboardGridBarComponent
         */
        _this.userChangedFilter = false;
        return _this;
    }
    /**
     * Gets the format by the filtername
     * @param {string} filter the filtername
     * @type {EntityFormat}
     * @memberOf PlanboardGridBarComponent
     */
    PlanboardGridBarComponent.prototype.getFilterFormat = function (filter) {
        return this.gridService.getFormat(filter);
    };
    Object.defineProperty(PlanboardGridBarComponent.prototype, "filterEntity", {
        /**
         * Gets the entity used as filter for the shown resources.
         * @type {Entity}
         * @memberOf PlanboardGridBarComponent
         */
        get: function () {
            return this._filterEntity;
        },
        /**
         * Sets the entity used as filter for the shown resources.
         * @memberOf PlanboardGridBarComponent
         */
        set: function (value) {
            if (this._filterEntity === value) {
                return;
            }
            this._filterEntity = value;
            this.filterEntityChanged.emit(value);
            if (value === null) {
                this.localStorageService.deleteProperty('local', 'planboard-filter-entity');
            }
            else {
                this.localStorageService.updateProperty('local', 'planboard-filter-entity', value['Oid']);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(PlanboardGridBarComponent.prototype, "team", {
        /**
         * Gets the team used as filter and needed for certain plugins.
         * @readonly
         * @type {Entity}
         * @memberOf PlanboardGridBarComponent
         */
        get: function () {
            return this._filterEntity != null && this._filterEntity.entityType.shortName === 'Team' ? this._filterEntity : null;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * The current source to select a filter entity from.
     * @returns The name of the current source.
     * @memberOf PlanboardGridBarComponent
     */
    PlanboardGridBarComponent.prototype.currentFilterSource = function () {
        return this.localStorageService.readProperty('local', 'grid-bar-filter-source', 'TEAM', this.filterSources);
    };
    /**
     * Select the next filter source
     * @memberOf PlanboardGridBarComponent
     */
    PlanboardGridBarComponent.prototype.nextFilterSource = function () {
        // Get the current filter source
        var currentFilterSource = this.currentFilterSource();
        // Find the position of the current filter source
        var position = this.filterSources.indexOf(currentFilterSource) + 1;
        // Last in list
        if (position === this.filterSources.length) {
            position = 0;
        }
        // Update the local storage
        this.localStorageService.updateProperty('local', 'grid-bar-filter-source', this.filterSources[position], this.filterSources);
    };
    /**
     * Handler for change of the filter by user.
     * @param {Event} event The selection changed event.
     */
    PlanboardGridBarComponent.prototype.filterChosen = function (event) {
        this.userChangedFilter = true;
    };
    /**
     * A lifecycle hook that is called after Angular has fully initialized the component
     * @memberOf PlanboardGridBarComponent
     */
    PlanboardGridBarComponent.prototype.ngAfterViewInit = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var filterSource, storedFilterOid, typeEntityFilter, dataSet, classID, found;
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        filterSource = this.currentFilterSource();
                        storedFilterOid = this.localStorageService.readProperty('local', 'planboard-filter-entity', undefined);
                        if (!storedFilterOid) return [3 /*break*/, 2];
                        typeEntityFilter = 'Team';
                        dataSet = new DataSet(this.domainService);
                        classID = storedFilterOid.substr(0, 2);
                        switch (classID) {
                            case '22': // Team
                                dataSet.sourceName = 'TeamItems';
                                typeEntityFilter = 'Team';
                                filterSource = this.filterSources[0];
                                break;
                            case '12': // Function
                                dataSet.sourceName = 'FunctionItems';
                                typeEntityFilter = 'Function';
                                filterSource = this.filterSources[1];
                                break;
                            case '32': // Region Location
                                typeEntityFilter = 'Location';
                                dataSet.sourceName = 'Regions';
                                filterSource = this.filterSources[2];
                                break;
                            case 'I2': // Resource
                                dataSet.sourceName = 'ResourceItems';
                                typeEntityFilter = 'Resource';
                                filterSource = this.filterSources[3];
                                break;
                            case 'O1': // Project
                                dataSet.sourceName = 'ProjectItems';
                                typeEntityFilter = 'Project';
                                filterSource = this.filterSources[4];
                                break;
                            default:
                                // Filter source is niet ondersteund.
                                this.filterEntity = null;
                                return [2 /*return*/];
                        }
                        dataSet.filter = new PropertyFilter(dataSet, 'Oid', FilterOperators.Equals, storedFilterOid);
                        return [4 /*yield*/, dataSet.load(typeEntityFilter)];
                    case 1:
                        found = _a.sent();
                        if (found.results.length > 0) {
                            this.filterEntity = found.results[0];
                        }
                        else {
                            this._filterEntity = null;
                            this.localStorageService.deleteProperty('local', 'planboard-filter-entity');
                        }
                        if (filterSource !== this.currentFilterSource()) {
                            // filterSource laten kloppen met filterEntity.
                            window.setTimeout(function () {
                                _this.localStorageService.updateProperty('local', 'grid-bar-filter-source', filterSource, _this.filterSources);
                            }, 500);
                        }
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    return PlanboardGridBarComponent;
}(GridBarBaseComponent));
export { PlanboardGridBarComponent };
